import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { isObjectNotEmpty } from "helpers/functions";
import { editSection, getSection } from "store/actions";
import { toastErrorMessage } from "helpers/toaster/toastErrorMessage";
import Loader from "components/shared/Loader";
import ImageUpload from "components/shared/ImageUpload";
import SaveChangesButton from "components/shared/SaveChangesButton";

const Project = () => {
  const { formatMessage, locale } = useIntl();
  const dispatch = useDispatch();
  const [selectedImages, setSelectedImages] = useState(
    Array(3).fill({ preview: null, path: "" }) // Initialize an array with 3 empty image objects
  );
  const { loading, error, section } = useSelector((state) => state.section);

  const {
    register,
    reset,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      section,
    },
  });

  useEffect(() => {
    dispatch(getSection({ page: "about", slug: "project" }));
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toastErrorMessage(formatMessage({ id: "sendingErrorMessage" }), locale);
    }
  }, [error, formatMessage, locale]);

  useEffect(() => {
    reset(section);
  }, [reset, section]);

  const onSubmit = (data) => {
    console.log("data >>>", data);

    const itemsWithoutImage = data.images.filter((image) => image === null);
    console.log("itemsWithoutImage >>>", itemsWithoutImage);

    if (itemsWithoutImage.length > 2) {
      toastErrorMessage(formatMessage({ id: "imageErrorMessage" }), locale);
      return;
    }

    dispatch(
      editSection({
        slug: "project",
        page: "about",
        data: { section: data },
      })
    );
  };

  useEffect(() => {
    if (section.images) {
      const imagesToSet = section.images.map((img) => ({
        preview: null, // or set to img if you have a way to display existing images
        path: img,
      }));
      setSelectedImages(imagesToSet);
      imagesToSet.forEach((img, index) => {
        setValue(`images[${index}]`, img.path);
      });
    }
    setValue(`sectionTitle.ar`, section?.sectionTitle?.ar || "");
    setValue(`sectionTitle.en`, section?.sectionTitle?.en || "");
    setValue(`sectionDescription.ar`, section?.sectionDescription?.ar || "");
    setValue(`sectionDescription.en`, section?.sectionDescription?.en || "");
  }, [section, setValue]);

  if (loading) return <Loader />;

  return (
    <div className="card">
      <div className="card-body">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <div className="card-head my-5">
              <h4>
                <FormattedMessage id={"project"} />
              </h4>
            </div>
            <Row>
              <h5 className="mb-3">
                <FormattedMessage id={"images"} />
              </h5>
              {selectedImages.map((image, index) => (
                <ImageUpload
                  key={index}
                  selectedImage={{
                    path: selectedImages[index].path,
                  }}
                  setSelectedImage={(image) => {
                    setSelectedImages((prevImages) => {
                      const updatedImages = [...prevImages];
                      updatedImages[index].path = image.path;
                      return updatedImages;
                    });
                    setValue(`images[${index}]`, image.path); // Update the form state
                  }}
                />
              ))}
            </Row>

            <Col sx={12}>
              <div className="add-multi-component mt-4">
                <Row>
                  {/* Arabic Title Input */}
                  <Col xs={12} xl={6}>
                    <div className="form-group">
                      <h5>
                        <FormattedMessage id={"title"} />
                        (<FormattedMessage id={"arabic-language"} />)
                      </h5>
                      <input
                        name={`sectionTitle.ar`}
                        type="text"
                        className="form-control form-outline"
                        {...register(`sectionTitle.ar`, {
                          required: formatMessage({ id: "requiredField" }),
                          minLength: {
                            value: 3,
                            message: formatMessage({ id: "minLength" }),
                          },
                          maxLength: {
                            value: 40,
                            message: formatMessage({ id: "maxLength" }),
                          },
                        })}
                      />
                      {errors?.sectionTitle?.ar?.message && (
                        <p className="error-hint">
                          {errors?.sectionTitle?.ar?.message}
                        </p>
                      )}
                    </div>
                  </Col>

                  {/* English Title Input */}
                  <Col xs={12} xl={6}>
                    <div className="form-group">
                      <h5>
                        <FormattedMessage id={"title"} />
                        (<FormattedMessage id={"english-language"} />)
                      </h5>
                      <input
                        name={`sectionTitle.en`}
                        type="text"
                        className="form-control form-outline"
                        {...register(`sectionTitle.en`, {
                          required: formatMessage({ id: "requiredField" }),
                          minLength: {
                            value: 3,
                            message: formatMessage({ id: "minLength" }),
                          },
                          maxLength: {
                            value: 40,
                            message: formatMessage({ id: "maxLength" }),
                          },
                        })}
                      />
                      {errors?.sectionTitle?.en?.message && (
                        <p className="error-hint">
                          {errors?.sectionTitle?.en?.message}
                        </p>
                      )}
                    </div>
                  </Col>

                  {/* Arabic Description Input */}
                  <Col xs={12} xl={6}>
                    <div className="form-group">
                      <h5>
                        <FormattedMessage id={"description"} />
                        (<FormattedMessage id={"arabic-language"} />)
                      </h5>
                      <textarea
                        name={`sectionDescription.ar`}
                        className="form-control form-outline"
                        {...register(`sectionDescription.ar`, {
                          required: formatMessage({ id: "requiredField" }),
                          minLength: {
                            value: 3,
                            message: formatMessage({ id: "minLength" }),
                          },
                          maxLength: {
                            value: 200,
                            message: formatMessage({ id: "maxLength" }),
                          },
                        })}
                      />
                      {errors?.sectionDescription?.ar?.message && (
                        <p className="error-hint">
                          {errors?.sectionDescription?.ar?.message}
                        </p>
                      )}
                    </div>
                  </Col>

                  {/* English Description Input */}
                  <Col xs={12} xl={6}>
                    <div className="form-group ">
                      <h5>
                        <FormattedMessage id={"description"} />
                        (<FormattedMessage id={"english-language"} />)
                      </h5>
                      <textarea
                        name={`sectionDescription.en`}
                        className="form-control form-outline"
                        {...register(`sectionDescription.en`, {
                          required: formatMessage({ id: "requiredField" }),
                          minLength: {
                            value: 3,
                            message: formatMessage({ id: "minLength" }),
                          },
                          maxLength: {
                            value: 200,
                            message: formatMessage({ id: "maxLength" }),
                          },
                        })}
                      />
                      {errors?.sectionDescription?.en?.message && (
                        <p className="error-hint">
                          {errors?.sectionDescription?.en?.message}
                        </p>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
          <div className="mt-4">
            <SaveChangesButton />
          </div>
        </form>
      </div>
    </div>
  );
};

export default Project;
